import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';

interface AnswerSheet {
  id: string;
  ShikakuName: string;
  TotalScore: number;
  CreateDate: any;
  Fin: boolean;
  UserName: string;
  [key: string]: any;
}

export default function Results() {
  const navigate = useNavigate();
  const [results, setResults] = useState<AnswerSheet[]>([]);
  const [loading, setLoading] = useState(false);

  const { userId } = useAuth();

  useEffect(() => {
    const fetchResults = async () => {
      if (!userId) {
        setResults([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setResults([]);

      try {
        const userRef = doc(db, 'User', userId);
        const q = query(
          collection(db, 'AnswerSheet'),
          where('UserRef', '==', userRef)
        );

        const querySnapshot = await getDocs(q);
        const sheets = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as AnswerSheet));

        sheets.sort((a, b) => {
          const dateA = a.CreateDate?.toDate ? a.CreateDate.toDate() : new Date(0);
          const dateB = b.CreateDate?.toDate ? b.CreateDate.toDate() : new Date(0);
          return dateB.getTime() - dateA.getTime();
        });

        setResults(sheets);
      } catch (error) {
        console.error("Error fetching results: ", error);
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [userId]);

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <header className="bg-white sticky top-0 z-10 border-b border-gray-100 shadow-sm">
        <div className="max-w-md mx-auto px-4 h-14 flex items-center">
          <button
            onClick={() => navigate('/home')}
            className="w-10 h-10 flex items-center justify-center -ml-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i className="ri-arrow-left-s-line text-2xl"></i>
          </button>
          <h1 className="flex-1 text-center font-bold text-gray-800 pr-8 text-base">
            試験結果一覧
          </h1>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-6">
        <p className="text-sm text-gray-500 mb-6">これまでの受験履歴を確認できます</p>

        {loading ? (
          <div className="text-center py-10 text-gray-500">読み込み中...</div>
        ) : (
          <div className="space-y-4">
            {results.map((result) => {
              const isPassed = result.TotalScore >= 70;
              const date = result.CreateDate?.toDate
                ? result.CreateDate.toDate().toLocaleDateString('ja-JP', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })
                : '不明';

              return (
                <div
                  key={result.id}
                  onClick={() => navigate(`/exam-result/${result.id}`)}
                  className="bg-white rounded-2xl p-5 shadow-sm cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${isPassed
                      ? 'bg-blue-100 text-blue-600'
                      : 'bg-gray-100 text-gray-600'
                      }`}>
                      {isPassed ? '合格' : '不合格'}
                    </span>
                  </div>

                  <div className="mb-3">
                    <p className="text-xs text-gray-500 mb-1">資格名</p>
                    <h3 className="font-bold text-gray-800 text-base">{result.ShikakuName || '不明な資格'}</h3>
                  </div>

                  <div className="border-t border-gray-100 pt-3">
                    <div className="grid grid-cols-2 gap-4 text-xs">
                      <div>
                        <p className="text-gray-500 mb-1">受験日</p>
                        <p className="text-gray-800 font-medium">{date}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 mb-1">得点</p>
                        <p className="text-gray-800 font-medium">{result.TotalScore}点 / 100点</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {results.length === 0 && !loading && (
          <div className="bg-white rounded-2xl p-8 shadow-sm text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
              <i className="ri-file-list-line text-gray-400 text-2xl"></i>
            </div>
            <p className="text-gray-500 mb-4">該当する受験履歴が見つかりません</p>
            <button
              onClick={() => navigate('/select-exam')}
              className="bg-[#F4B183] text-white px-6 py-3 rounded-xl font-bold hover:bg-[#E5A072] transition-colors text-sm"
            >
              試験を受ける
            </button>
          </div>
        )}

        <div className="mt-8 bg-white rounded-2xl p-5 shadow-sm">
          <div className="flex items-start gap-3">
            <i className="ri-information-line text-[#F4B183] text-xl shrink-0 mt-0.5"></i>
            <div>
              <h3 className="font-bold text-gray-800 mb-1 text-sm">受験履歴について</h3>
              <p className="text-xs text-gray-600 leading-relaxed">
                合格の場合の認定証は自動的に発送されます。不合格の際の再試験については事務局に公式LINEよりお問合せください。
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
